import { Actions, Subjects } from '@/client/users';
import {
  AppRoutesPaths,
  DashboardType,
  LAST_SELECTED_DASHBOARD,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { useOperationLogs } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentDashboard } from '@/store/features/dashboard';
import { selectCurrentUser } from '@/store/features/users';
import { FlexContainer } from '@/ui/styled-ui';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GetHelpOverlayPanel } from './GetHelpOverlayPanel';

const StyledContainer = styled(FlexContainer)`
  height: 100%;
`;

const StyledAvatar = styled(Avatar)`
  border-radius: 50%;
  background: var(--white-main);
  cursor: pointer;
  &:hover {
    background: var(--gray-dark);
  }
  .p-avatar-text {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
  }
`;

const StyledMenu = styled(Menu)`
  margin: 4px 0;
  .separator .p-menuitem-link {
    padding: 0;
    border-bottom: 1px solid var(--gray-dark);
  }
  &.p-menu.p-menu-overlay .p-menuitem {
    border: 0;
  }
`;

const StyledImg = styled.img`
  cursor: pointer;
  max-width: 140px;
  max-height: 40px;
`;

const StyledLink = styled.div`
  color: var(--white-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;
  display: flex;
  align-items: center;
  &:hover {
    color: var(--purple-light);
    cursor: pointer;
  }
  .pi-question-circle {
    font-size: var(--medium-font-size);
  }
`;

const BellButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--white-main);
  padding: 0;
  cursor: pointer;
  margin: 0 var(--xsmall-padding);

  i {
    font-size: var(--medium-font-size);
  }

  &:hover {
    color: var(--purple-light);
  }
`;

const BellBadge = styled.span`
  position: absolute;
  top: -6px;
  left: 8px;
  min-width: 16px;
  height: 16px;
  padding: 0 var(--xxsmall-padding);
  border-radius: 16px;
  background: var(--red-dark);
  color: var(--white-main);
  font-size: var(--xsmall-font-size);
  line-height: 16px;
`;

export const TopbarMenu: React.FC<{ fallbackLogo?: string }> = ({
  fallbackLogo,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const selectedDashboard = useAppSelector(selectCurrentDashboard);
  const isAdminView = selectedDashboard === DashboardType.ADMIN;
  const { can } = usePermission();
  const { operationLogs } = useOperationLogs(currentAccount?.id);
  const errorIssueCount =
    operationLogs?.result?.reduce(
      (sum, log) => sum + (log.errorCount ?? 0),
      0,
    ) ?? 0;

  const op = useRef<OverlayPanel>(null);

  const itemActionMenu = useRef<any>(null);

  const toggleItemActionMenu = (event: any) => {
    if (!itemActionMenu.current) {
      return;
    }

    itemActionMenu.current.toggle(event);
  };

  const logoRedirect = () => {
    if (localStorage.getItem(LAST_SELECTED_DASHBOARD) === DashboardType.ADMIN) {
      navigate(RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD]());
    } else {
      navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]());
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: t('generic.profile.settings'),
      icon: 'pi pi-user',
      command: () => {
        if (
          localStorage.getItem(LAST_SELECTED_DASHBOARD) === DashboardType.ADMIN
        ) {
          navigate(RedirectPaths[RedirectPathsEnum.PROFILE_SETTINGS]());
        } else {
          navigate(
            RedirectPaths[RedirectPathsEnum.LEARNER_PROFILE_SETTINGS](),
            {
              state: { from: location.pathname },
            },
          );
        }
      },
    },
  ];

  const showErrorBell =
    isAdminView && can(Actions.READ, Subjects.USERS) && errorIssueCount > 0;

  const errorCountLabel =
    errorIssueCount > 99 ? '99+' : String(errorIssueCount);

  menuItems.push(
    ...[
      {
        items: [
          { separator: true, className: 'separator' },
          {
            label: t('generic.logout'),
            icon: 'pi pi-sign-out',
            command: () => {
              navigate(AppRoutesPaths.LOGOUT);
            },
          },
        ],
      },
    ],
  );

  return (
    <>
      <StyledMenu
        model={menuItems}
        popup
        ref={itemActionMenu}
        id="overlay_menu"
      />
      <StyledContainer justify="space-between" className="px-4">
        <StyledImg
          src={currentAccount?.platformSettings?.logo || fallbackLogo}
          alt={currentAccount?.platformSettings?.name || 'CyberPilot'}
          onClick={logoRedirect}
        />

        <FlexContainer justify="flex-end" gap={16}>
          {showErrorBell && (
            <BellButton
              type="button"
              aria-label={t('errorLog.title')}
              onClick={() =>
                navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]())
              }
            >
              <i className="pi pi-bell" />
              <BellBadge>{errorCountLabel}</BellBadge>
            </BellButton>
          )}
          {isAdminView && !currentAccount?.isSystem && user && (
            <>
              <StyledLink onClick={(e) => op.current?.toggle(e)}>
                <i className="pi pi-question-circle pr-2" />
                <span>{t('topbar.getHelp')}</span>
              </StyledLink>
              <GetHelpOverlayPanel op={op} />
            </>
          )}
          {can(Actions.READ, Subjects.ADMIN_DASHBOARD) && (
            <StyledLink
              onClick={() =>
                isAdminView
                  ? navigate(
                      RedirectPaths[
                        RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE
                      ](),
                    )
                  : navigate(RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD]())
              }
            >
              <i
                className={
                  isAdminView ? 'pi pi-sign-in pr-2' : 'pi pi-sign-out pr-2'
                }
              />
              <span>
                {isAdminView ? t('profile.goLearner') : t('profile.goAdmin')}
              </span>
            </StyledLink>
          )}
          {user && (
            <StyledAvatar
              label={user?.firstName?.slice(0, 1).toUpperCase()}
              shape="circle"
              onClick={toggleItemActionMenu}
            />
          )}
        </FlexContainer>
      </StyledContainer>
    </>
  );
};
